import React, { useState, useEffect } from 'react';
import { BarChart3, FileText, Calendar, Trash2, Eye, Clock } from 'lucide-react';
import './Documents.css';

const ANALYSES_STORAGE_KEY = 'researchWizard_savedAnalyses';
const DOCUMENTS_STORAGE_KEY = 'researchWizard_documents';

const SavedAnalysesList = ({ onSelectAnalysis }) => {
  const [analyses, setAnalyses] = useState([]);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    const savedAnalyses = localStorage.getItem(ANALYSES_STORAGE_KEY);
    if (savedAnalyses) {
      try {
        setAnalyses(JSON.parse(savedAnalyses));
      } catch (e) {
        console.error('Failed to load analyses:', e);
      }
    }

    const savedDocs = localStorage.getItem(DOCUMENTS_STORAGE_KEY);
    if (savedDocs) {
      try {
        setDocuments(JSON.parse(savedDocs));
      } catch (e) {
        console.error('Failed to load documents:', e);
      }
    }
  }, []);

  const getDocumentTitle = (documentId) => {
    const doc = documents.find(d => d.id === documentId);
    return doc?.title || 'Unknown Document';
  };

  const getDocument = (documentId) => {
    return documents.find(d => d.id === documentId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const deleteAnalysis = (analysisId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this saved analysis?')) {
      const updated = analyses.filter(a => a.id !== analysisId);
      setAnalyses(updated);
      localStorage.setItem(ANALYSES_STORAGE_KEY, JSON.stringify(updated));
    }
  };

  const handleSelectAnalysis = (analysis) => {
    const doc = getDocument(analysis.documentId);
    if (doc) {
      onSelectAnalysis(doc, analysis.lastMethod);
    } else {
      alert('The document associated with this analysis no longer exists.');
    }
  };

  const getMethodLabel = (method) => {
    const labels = {
      sentiment: 'Sentiment Analysis',
      thematic: 'Thematic Analysis',
      content: 'Content Analysis',
      narrative: 'Narrative Analysis',
      discourse: 'Discourse Analysis',
      grounded: 'Grounded Theory',
      phenomenological: 'Phenomenological Analysis'
    };
    return labels[method] || method;
  };

  const getMethodColor = (method) => {
    const colors = {
      sentiment: '#22c55e',
      thematic: '#9333ea',
      content: '#3b82f6',
      narrative: '#f97316',
      discourse: '#14b8a6',
      grounded: '#6366f1',
      phenomenological: '#ec4899'
    };
    return colors[method] || '#6366f1';
  };

  return (
    <div className="saved-analyses-section">
      <div className="section-header">
        <BarChart3 size={22} />
        <h3>Recent Qualitative Analyses</h3>
      </div>
      <p className="section-desc">
        {analyses.length > 0 
          ? 'Continue working on your previous analyses' 
          : 'Your saved analyses will appear here after you analyze a document'}
      </p>
      
      {analyses.length === 0 ? (
        <div className="empty-analyses-state">
          <Clock size={32} />
          <p>No analyses yet</p>
          <span>Upload a document and run an analysis to get started</span>
        </div>
      ) : (
      <div className="analyses-list">
        {analyses.slice(0, 5).map(analysis => (
          <div 
            key={analysis.id} 
            className="analysis-card"
            onClick={() => handleSelectAnalysis(analysis)}
          >
            <div className="analysis-card-icon" style={{ background: getMethodColor(analysis.lastMethod) }}>
              <BarChart3 size={20} />
            </div>
            
            <div className="analysis-card-content">
              <div className="analysis-card-title">
                <FileText size={14} />
                <span>{getDocumentTitle(analysis.documentId)}</span>
              </div>
              <div className="analysis-card-method" style={{ color: getMethodColor(analysis.lastMethod) }}>
                {getMethodLabel(analysis.lastMethod)}
              </div>
              <div className="analysis-card-meta">
                <span><Calendar size={12} /> {formatDate(analysis.lastAccessed)}</span>
              </div>
            </div>

            <div className="analysis-card-actions">
              <button 
                className="btn-icon view"
                onClick={(e) => { e.stopPropagation(); handleSelectAnalysis(analysis); }}
                title="Continue Analysis"
              >
                <Eye size={16} />
              </button>
              <button 
                className="btn-icon delete"
                onClick={(e) => deleteAnalysis(analysis.id, e)}
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export const saveAnalysisSession = (documentId, method) => {
  const saved = localStorage.getItem(ANALYSES_STORAGE_KEY);
  let analyses = [];
  
  if (saved) {
    try {
      analyses = JSON.parse(saved);
    } catch (e) {
      console.error('Failed to load analyses:', e);
    }
  }

  const existingIndex = analyses.findIndex(a => a.documentId === documentId);
  
  if (existingIndex >= 0) {
    analyses[existingIndex].lastMethod = method;
    analyses[existingIndex].lastAccessed = new Date().toISOString();
  } else {
    analyses.unshift({
      id: `analysis-${Date.now()}`,
      documentId,
      lastMethod: method,
      lastAccessed: new Date().toISOString(),
      createdAt: new Date().toISOString()
    });
  }

  analyses = analyses.slice(0, 10);
  
  localStorage.setItem(ANALYSES_STORAGE_KEY, JSON.stringify(analyses));
};

export default SavedAnalysesList;
